// Central API client for the Smart Vision ticket backend (.NET 8).
// Defaults to deployed API. Override with REACT_APP_API_BASE when needed.
const DEFAULT_API_BASE = 'https://koplpune.in/warehouse';
const API_BASE = (process.env.REACT_APP_API_BASE || DEFAULT_API_BASE).replace(/\/+$/, '');

// ---- enum maps (must match the C# enums) ----
export const PROBLEM_AREAS = [
  { value: 0, label: 'CCTV', key: 'CCTV' },
  { value: 1, label: 'Biometric', key: 'Biometric' },
  { value: 2, label: 'Electric Work', key: 'ElectricWork' },
  { value: 3, label: 'New Requirement', key: 'NewRequirement' },
];

export const PRIORITIES = [
  { value: 3, label: 'Critical', cls: 'p-critical' },
  { value: 2, label: 'High', cls: 'p-high' },
  { value: 1, label: 'Medium', cls: 'p-medium' },
  { value: 0, label: 'Low', cls: 'p-low' },
];

export const STATUSES = [
  { value: 0, label: 'Open', key: 'Open' },
  { value: 1, label: 'In Progress', key: 'InProgress' },
  { value: 2, label: 'Resolved', key: 'Resolved' },
  { value: 3, label: 'Closed', key: 'Closed' },
];

// ---- token storage ----
const TOKEN_KEY = 'sv_token';
const USER_KEY = 'sv_user';

export const auth = {
  get token() { return localStorage.getItem(TOKEN_KEY); },
  get user() {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); }
    catch { return null; }
  },
  save(resp) {
    if (resp.token) localStorage.setItem(TOKEN_KEY, resp.token);
    localStorage.setItem(USER_KEY, JSON.stringify({
      userId: resp.userId, mobile: resp.mobile, name: resp.name,
      companyName: resp.companyName, clientLocation: resp.clientLocation,
      role: resp.role, mustResetPin: resp.mustResetPin,
    }));
  },
  patchUser(patch) {
    const u = auth.user || {};
    localStorage.setItem(USER_KEY, JSON.stringify({ ...u, ...patch }));
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  get isAdmin() { return auth.user?.role === 'Admin'; },
};

// ---- absolute URL for stored image paths ----
export function imageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

async function request(path, { method = 'GET', body, isForm = false } = {}) {
  const headers = {};
  if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`;
  if (!isForm && body) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${API_BASE}/api${path}`, {
    method,
    headers,
    body: isForm ? body : (body ? JSON.stringify(body) : undefined),
  });

  if (res.status === 401) {
    auth.clear();
    if (!path.includes('/auth/login')) window.location.hash = '#/login';
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // Dev proxy failures can return plain text (not JSON).
      data = { raw: text };
    }
  }

  if (!res.ok) {
    const raw = typeof data?.raw === 'string' ? data.raw.trim() : '';
    if (raw.toLowerCase().startsWith('proxy error')) {
      throw new Error(
        'Cannot reach backend API. Check REACT_APP_API_BASE and server availability.'
      );
    }
    throw new Error(data?.message || raw || `Request failed (${res.status})`);
  }

  // A successful API response should be JSON when a body is present.
  if (text && data?.raw) {
    throw new Error('API returned non-JSON response.');
  }
  return data;
}

// ---- Auth ----
export const api = {
  login: (payload) => request('/auth/login', { method: 'POST', body: payload }),
  resetPin: (newPin) => request('/auth/reset-pin', { method: 'POST', body: { newPin } }),
  me: () => request('/auth/me'),

  // ---- Tickets ----
  listTickets: (filters = {}) => {
    const q = new URLSearchParams();
    if (filters.status) q.set('status', filters.status);
    if (filters.area) q.set('area', filters.area);
    const qs = q.toString();
    return request(`/tickets${qs ? `?${qs}` : ''}`);
  },
  getTicket: (id) => request(`/tickets/${id}`),
  createTicket: (payload) => request('/tickets', { method: 'POST', body: payload }),
  updateStatus: (id, status, remark) =>
    request(`/tickets/${id}/status`, { method: 'POST', body: { status, remark } }),
  uploadImages: (id, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return request(`/tickets/${id}/images`, { method: 'POST', body: fd, isForm: true });
  },
};
